import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../../basic';
import { Spinner, CollapseBlock } from '../../../../../common';
import AttendanceEventForm from './AttendanceEventForm.jsx';
import {
  useSaveAttendanceEventMutation,
  useGetAttendanceScheduleQuery,
} from '../../../../../../redux/services/attendance';
import { useGetRoomsQuery } from '../../../../../../redux/services/rooms';
import {
  useGetTeachersQuery,
  useGetUserQuery,
} from '../../../../../../redux/services/users';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';
import { routes } from '../../../../../../utils/helpers/ApiClient';
import dayjs from 'dayjs';

const AttendanceEventEdit = ({ mode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((reduxStore) => reduxStore.auth.user);

  const isSchedule = mode === 'schedule';

  const { saveSuccess: toastSaveSuccess, apiErrorMessage } = useToastMessages();

  const [saveAttendanceEvent] = useSaveAttendanceEventMutation();

  const { data: teacher_data, isLoading: isLoadingUsers } = useGetUserQuery(
    user.person_id,
  );

  const { data: teachers, isLoadingTeachers } = useGetTeachersQuery();

  const { data: scheduleBlocks, isLoadigTeacherSubjects } =
    useGetAttendanceScheduleQuery(teacher_data?.teacher?.uidnumber, {
      skip: isSchedule === false,
    });

  const { data: rooms, isLoading: isLoadingRooms } = useGetRoomsQuery();

  const [initials, setInitials] = useState({
    blockId: undefined,
    selectedBlock: undefined,
    title: undefined,
    room: undefined,
    created_by: `${user.title_before ?? ''} ${user.title_after ?? ''} ${user.last_name} ${user.first_name}`,
    teacher_id: undefined,
    day: undefined,
    time: [undefined, undefined],
  });

  const teachersForSelect = useMemo(() => {
    if (!teachers) return [];
    return teachers
      .map((person) => {
        return {
          label: person.full_name,
          value: person.uidnumber,
          last_name: person.person.last_name,
        };
      })
      .sort((a, b) => a.last_name.localeCompare(b.last_name));
  }, [teachers]);

  const roomsForSelect = useMemo(() => {
    if (!rooms) return [];
    return rooms
      .map((room) => {
        return {
          label: room.title,
          value: room.title,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rooms]);

  const daysInWeek = {
    1: 'Pondelok',
    2: 'Utorok',
    3: 'Streda',
    4: 'Štvrtok',
    5: 'Piatok',
  };

  const scheduleBlocksForSelect = useMemo(() => {
    if (!scheduleBlocks) return [];

    return scheduleBlocks
      .map((block) => {
        const day = daysInWeek[block.day];
        const start = block.start_at;
        const blocksCount = block.blocks.split(', ').length;
        const end = dayjs(block.start_at, 'HH:mm')
          .add(blocksCount, 'hour')
          .subtract(blocksCount * 10, 'minute')
          .format('HH:mm');
        return {
          label: `${day} (${start} - ${end}): ${block.code} - ${block.course}`,
          value: `${block.day}${block.block}`,
          code: `${block.code}`,
        };
      })
      .sort((a, b) => a.value - b.value);
  }, [scheduleBlocks, daysInWeek]);

  const title =
    (isSchedule && t('Vytváranie udalosti z rozvrhu')) ||
    t('Vytváranie vlastnej udalosti');

  if (
    isLoadingTeachers ||
    isLoadingUsers ||
    isLoadigTeacherSubjects ||
    isLoadingRooms
  ) {
    return <Spinner />;
  }

  const changeScheduleBlock = (scheduleBlockId) => {
    const filteredBlock = scheduleBlocks.find(
      (block) => `${block.day}${block.block}` === scheduleBlockId,
    );

    if (filteredBlock) {
      const day = daysInWeek[filteredBlock.day];
      const start = dayjs(filteredBlock.start_at, 'HH:mm');
      const blocksCount = filteredBlock.blocks.split(', ').length;
      const end = dayjs(filteredBlock.start_at, 'HH:mm')
        .add(blocksCount, 'hour')
        .subtract(blocksCount * 10, 'minute');

      setInitials((prevState) => ({
        ...prevState, // Preserve other values
        blockId: `${filteredBlock.day}${filteredBlock.block}`,
        title: `${filteredBlock.type}`,
        selectedBlock: {
          label: `${day} (${start.format('HH:mm')} - ${end.format('HH:mm')}): ${filteredBlock.code} - ${filteredBlock.course}`,
          value: `${filteredBlock.day}${filteredBlock.block}`,
          code: `${filteredBlock.code}`,
        },
        room: filteredBlock.room,
        day: filteredBlock.day,
        time: [start, end],
      }));
    }
  };

  const createEvent = async (values) => {
    const start = values.date
      .hour(values.time[0].format('HH'))
      .minute(values.time[0].format('mm'))
      .second(0);
    const end = values.date
      .hour(values.time[1].format('HH'))
      .minute(values.time[1].format('mm'))
      .second(0);
    const format = 'YYYY-MM-DD HH:mm:ss';

    const body = {
      title: values.title,
      room: values.room ?? null,
      subject:
        values.selectedBlock === undefined ? null : values.selectedBlock.code,
      teacher_id: values.teacher_id ?? null,
      started_at: start.format(format),
      ended_at: end.format(format),
    };

    try {
      const result = await saveAttendanceEvent({ body }).unwrap();
      toastSaveSuccess();
      navigate(`${routes.cmsAttendanceEventDetail}/${result.id}`);
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={routes.cmsAttendanceSchedule}
        backUrlTooltip={t('Rozvrh')}
        permissionsBasic={'attendance-events.schedule'} // TODO: Samuel Change permissions
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={title}>
          <AttendanceEventForm
            mode={mode}
            teachers={teachersForSelect}
            rooms={roomsForSelect}
            scheduleBlocks={isSchedule ? scheduleBlocksForSelect : []}
            initials={initials}
            onChange={
              isSchedule
                ? (scheduleBlockId) => changeScheduleBlock(scheduleBlockId)
                : undefined
            }
            onSubmit={(values) => createEvent(values)}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default AttendanceEventEdit;
